import { Fragment, FunctionComponent } from 'react';
import Box from '@mui/material/Box';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { Theme } from '@mui/material/styles';
import { Pagination } from '../../types';
import { BlogListItem } from '../../types/Item';
import HomePaperPad from '../../../PaperPad';
import { BLOG_PAGINATION_LIMIT, CONTENT_MARGIN_TOP } from '../../../../config';
import { KnownBreakpoints } from '../../../../common/constants';
import { useRouter } from 'next/router';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import BlockTitle from '../../components/BlockTitle';
import Image from 'next/image';
import SeeMoreButton from '../../components/SeeMoreButton';
import getImageAttributes from '../../../../common/helpers/getImageAttributes';

const styles = {
    wrapper: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: `-${ CONTENT_MARGIN_TOP }px`,
        paddingBottom: '60px',
    },
    card: (theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        flex: '0 1 calc(33%)',
        maxWidth: '440px',
        height: '568px',
        boxSizing: 'border-box',
        padding: '40px 40px 23px',
        [theme.breakpoints.down(KnownBreakpoints.desktop)]: {
            flex: '0 1 calc(50% - 10px)',
            maxWidth: 'calc(50% - 10px)',
        },
        [theme.breakpoints.down(KnownBreakpoints.laptop)]: {
            height: 'unset',
        },
        [theme.breakpoints.down(KnownBreakpoints.tablet)]: {
            flex: '0 1 calc(100%)',
            maxWidth: '100%',
            padding: '20px',
        },
    }),
    blogContainer: {
        cursor: 'pointer',
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
        marginBottom: '30px',
    },
    image: {
        maxWidth: '100%',
        objectFit: 'cover',
    },
    title: {
        marginBottom: '16px',
    },
    text: (theme: Theme) => ({
        fontSize: theme.typography.fontSize,
        lineHeight: '24px',
    }),
    readButton: (theme: Theme) => ({
        position: 'absolute',
        left: 40,
        bottom: 23,
        [theme.breakpoints.down(KnownBreakpoints.laptop)]: {
            position: 'unset',
            marginTop: '16px',
        },
    }),
    loadMore: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginTop: '45px',
    },
    icon: {},
    button: (theme: Theme) => ({
        backgroundColor: theme.palette.info.light,
        width: '440px',
        height: '60px',
        '&:hover': {
            backgroundColor: theme.palette.info.light,
        },
    }),
    buttonIcon: (theme: Theme) => ({
        color: theme.palette.secondary.dark,
        transform: 'scaleX(-1)',
    }),
    buttonText: (theme: Theme) => ({
        color: theme.palette.secondary.dark,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '11.72px',
    }),
};

export interface BlogListProps {
    posts: BlogListItem[];
    pagination: Pagination;
}

const BlogList: FunctionComponent<BlogListProps> = ({ posts, pagination }) => {
    const router = useRouter();
    const { limit, hasMore } = pagination;

    const handleLoadMore = () => {
        return router.push(
            `/blog?limit=${ limit + BLOG_PAGINATION_LIMIT }`,
            undefined,
            { scroll: false },
        );
    };

    return <Box sx={ styles.wrapper }>
        { posts.map(post => {
            const img = getImageAttributes(post.image, 'medium');

            return <Fragment key={ post.id }>
                <HomePaperPad sx={ styles.card }>
                    <Box
                        sx={ styles.blogContainer }
                        onClick={ () => router.push(`blog/${post.slug}`) }
                    >
                        <Box sx={ styles.imageContainer }>
                            <Image
                                priority
                                alt={ img.alternativeText || '' }
                                src={ img.url || '' }
                                width={ img.width }
                                height={ 212 }
                                style={ styles.image as any }
                            />
                        </Box>
                        <BlockTitle title={ post.title } sx={ styles.title } />
                        <Typography component="span" sx={ styles.text }>
                            { post.text }
                        </Typography>
                    </Box>
                    <Box sx={ styles.readButton }>
                        <SeeMoreButton href={ `blog/${post.slug}` }>
                            Read article
                        </SeeMoreButton>
                    </Box>
                </HomePaperPad>
            </Fragment>;
        })}

        { hasMore && <Box sx={ styles.loadMore }>
            <Button
                disableRipple={ true }
                startIcon={ <ReplayOutlinedIcon sx={ styles.buttonIcon } /> }
                sx={ styles.button }
                onClick={ handleLoadMore }
                data-testid="btn-load-more-articles"
            >
                <Typography component="span" sx={ styles.buttonText }>
                    Load more articles...
                </Typography>
            </Button>
        </Box> }

    </Box>;
};

export default BlogList;
